import { getCoreRowModel, useReactTable } from "@tanstack/react-table";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Clock3Icon, Search2Icon } from "@/shared/icons";
import { TextInput } from "@/shared/ui";
import Pagination from "@/shared/ui/Pagination";
import { TableVirtualize } from "@/shared/widgets";
import { FactionEnum } from "@/domains/faction/constants/factionEnum";
import { HistoryTabsEnum } from "@/domains/faction/constants/historyTabsEnum";
import { fakeHistory } from "@/domains/faction/helpers/fakeHistory";
import useHistoryColumns from "@/domains/faction/hooks/useHistoryColumns";
import FactionTabsRow from "@/domains/faction/components/FactionTabsRow";

type Props = {
  faction: FactionEnum;
};

export default function FactionHistoryContainer({ faction }: Props) {
  const { t } = useTranslation("faction");

  const tabs = useMemo(
    () => [HistoryTabsEnum.PARTICIPANTS, HistoryTabsEnum.FINANCE, HistoryTabsEnum.WAREHOUSE],
    [],
  );

  const [inputValue, setInputValue] = useState("");
  const [activeTab, setActiveTab] = useState(tabs[0]);

  const { getParticipantsColumns, getFinanceColumns, getWarehouseColumns } = useHistoryColumns();

  const baseData = useMemo(() => {
    switch (activeTab) {
      case HistoryTabsEnum.PARTICIPANTS:
        return fakeHistory.participants;
      case HistoryTabsEnum.FINANCE:
        return fakeHistory.finance;
      case HistoryTabsEnum.WAREHOUSE:
        return fakeHistory.warehouse;
      default:
        return [];
    }
  }, [activeTab]);

  const columns = useMemo(() => {
    switch (activeTab) {
      case HistoryTabsEnum.PARTICIPANTS:
        return getParticipantsColumns();
      case HistoryTabsEnum.FINANCE:
        return getFinanceColumns();
      case HistoryTabsEnum.WAREHOUSE:
        return getWarehouseColumns();
      default:
        return [];
    }
  }, [activeTab, getParticipantsColumns, getFinanceColumns, getWarehouseColumns]);

  const table = useReactTable({
    data: baseData,
    columns,
    // state: { sorting },
    // onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    // getSortedRowModel: getSortedRowModel(),
  });

  return (
    <div className="flex flex-col h-full p-6">
      <FactionTabsRow
        title={t("faction.titles.history")}
        Icon={Clock3Icon}
        tabs={tabs}
        activeTab={activeTab}
        setActiveTab={setActiveTab}
        faction={faction}
        i18Path="faction.historyTabs"
      />
      <TextInput
        value={inputValue}
        onChange={setInputValue}
        maxLength={40}
        type="text"
        inputClassName="h-9.5 text-14"
        placeholder={t("faction.searchForUsers")}
        icon={<Search2Icon className="size-4.5" />}
      />
      <div className="relative flex flex-col grow min-h-0">
        <div className="relative grow min-h-0">
          <div className="absolute z-2 bottom-0 left-0 w-full h-10 bg-gradient-to-t from-dark-900 to-transparent pointer-events-none" />
          <div className="absolute z-2 bottom-0 left-0 w-full h-8 bg-gradient-to-t from-dark-900 to-transparent pointer-events-none" />
          <div className="absolute inset-0 overflow-x-clip w-[calc(100%+1.0625rem)]">
            <div className="h-full">
              <TableVirtualize
                table={table}
                isLoading={false}
                scrollbarPaddingBottom="pb-3.5"
                scrollbarPaddingRight="pr-[0.8125rem]"
              />
            </div>
          </div>
        </div>
      </div>
      <Pagination
        currentPage={1}
        totalPages={99}
        setCurrentPage={() => {}}
        className="ml-auto mt-4"
      />
    </div>
  );
}
